/** XeAgents.ai**/
import {
  Calendar,
  CalendarCheck,
  Bell,
  HelpCircle,
  PhoneForwarded,
  Moon,
  ClipboardList,
  Globe,
  UserPlus,
  Route,
  MessageSquare,
  PhoneMissed,
  Star,
  FileText,
  FilePenLine,
} from "lucide-react";
import { ComponentType } from "react";

export interface Agent {
  id: string;
  title: string;
  description: string;
  categories: string[];
  icon: ComponentType<{ className?: string }>;
  iconColor: string;
  bgColor: string;
}

export interface TabConfig {
  id: string;
  label: string;
  agentIds: string[];
}

/**
 * Healthcare use cases. The English copy here is the source; what renders is
 * useCases.agents.<id> / useCases.categories.<key> / useCases.tabs.<id> from
 * the landing namespace, so keep i18n/landing/en.json in step with this file.
 *
 * Every description has to be something the platform does
 * today. Booking is create-only (no reschedule or cancel yet), handoff is a
 * transfer to a number, and no card claims a patient-record or PMS update.
 */
export const agents: Record<string, Agent> = {
  appointmentBooking: {
    id: "appointmentBooking",
    title: "Appointment booking",
    description:
      "Takes booking requests by phone, collects the patient's details and books the appointment in your calendar.",
    categories: ["inbound", "appointments"],
    icon: Calendar,
    iconColor: "text-blue-600",
    bgColor: "bg-blue-50 dark:bg-blue-950/30",
  },
  patientEnquiries: {
    id: "patientEnquiries",
    title: "Patient enquiries",
    description:
      "Answers routine questions about hours, location and services from information you approve, and passes anything else to staff.",
    categories: ["inbound", "enquiries"],
    icon: HelpCircle,
    iconColor: "text-cyan-600",
    bgColor: "bg-cyan-50 dark:bg-cyan-950/30",
  },
  afterHours: {
    id: "afterHours",
    title: "After-hours and overflow calls",
    description:
      "Picks up calls your team can't reach, takes the details or books an appointment, and leaves a transcript and summary for your staff.",
    categories: ["inbound", "overflow"],
    icon: Moon,
    iconColor: "text-indigo-600",
    bgColor: "bg-indigo-50 dark:bg-indigo-950/30",
  },
  appointmentReminders: {
    id: "appointmentReminders",
    title: "Appointment reminders",
    description:
      "Calls patients ahead of their visit to confirm, and records who confirmed and who needs a person to follow up.",
    categories: ["outbound", "reminders"],
    icon: Bell,
    iconColor: "text-amber-600",
    bgColor: "bg-amber-50 dark:bg-amber-950/30",
  },
  followUpCalls: {
    id: "followUpCalls",
    title: "Follow-up calls",
    description:
      "Checks in after a visit or service using a script you define, and flags the calls that need a person.",
    categories: ["outbound", "followUps"],
    icon: CalendarCheck,
    iconColor: "text-emerald-600",
    bgColor: "bg-emerald-50 dark:bg-emerald-950/30",
  },
  staffHandoff: {
    id: "staffHandoff",
    title: "Handoff to your team",
    description:
      "Transfers the call to a number you choose when a person is needed, instead of the AI handling it alone.",
    categories: ["everyCall", "escalation"],
    icon: PhoneForwarded,
    iconColor: "text-rose-600",
    bgColor: "bg-rose-50 dark:bg-rose-950/30",
  },
  transcriptsOutcomes: {
    id: "transcriptsOutcomes",
    title: "Transcripts and outcomes",
    description:
      "Every call comes with a transcript, a summary and an outcome, so your team can see what was handled and what wasn't.",
    categories: ["everyCall", "reporting"],
    icon: ClipboardList,
    iconColor: "text-violet-600",
    bgColor: "bg-violet-50 dark:bg-violet-950/30",
  },
  multilingual: {
    id: "multilingual",
    title: "Multilingual calls",
    description:
      "Speaks with patients in their own language, with 11 languages detected automatically.",
    categories: ["everyCall", "accessibility"],
    icon: Globe,
    iconColor: "text-teal-600",
    bgColor: "bg-teal-50 dark:bg-teal-950/30",
  },
  patientRegistration: {
    id: "patientRegistration",
    title: "Patient registration",
    description:
      "Collects a new patient's name, contact details and reason for visit by phone, and gives your front desk the details with the transcript.",
    categories: ["inbound", "registration"],
    icon: UserPlus,
    iconColor: "text-indigo-600",
    bgColor: "bg-indigo-50 dark:bg-indigo-950/30",
  },
  departmentRouting: {
    id: "departmentRouting",
    title: "Department routing",
    description:
      "Asks what the caller needs and transfers them to the right department number you have set up.",
    categories: ["inbound", "escalation"],
    icon: Route,
    iconColor: "text-sky-600",
    bgColor: "bg-sky-50 dark:bg-sky-950/30",
  },
  callbackRequests: {
    id: "callbackRequests",
    title: "Callback requests",
    description:
      "Takes a message and a preferred callback time when nobody is free, and leaves it in the call summary for your staff.",
    categories: ["inbound", "enquiries"],
    icon: MessageSquare,
    iconColor: "text-pink-600",
    bgColor: "bg-pink-50 dark:bg-pink-950/30",
  },
  missedAppointments: {
    id: "missedAppointments",
    title: "Missed-appointment recovery",
    description:
      "Calls patients after a missed visit, without blame, and offers to rebook.",
    categories: ["outbound", "followUps"],
    icon: PhoneMissed,
    iconColor: "text-orange-600",
    bgColor: "bg-orange-50 dark:bg-orange-950/30",
  },
  patientFeedback: {
    id: "patientFeedback",
    title: "Patient feedback calls",
    description:
      "Runs a short experience survey on waiting time, staff and the visit overall. No clinical content.",
    categories: ["outbound", "feedback"],
    icon: Star,
    iconColor: "text-yellow-600",
    bgColor: "bg-yellow-50 dark:bg-yellow-950/30",
  },
  reportReady: {
    id: "reportReady",
    title: "Report-ready notifications",
    description:
      "Tells patients a report is ready and how to collect it. Never states results, and checks identity first.",
    categories: ["outbound", "reminders"],
    icon: FileText,
    iconColor: "text-cyan-600",
    bgColor: "bg-cyan-50 dark:bg-cyan-950/30",
  },
  pendingDetails: {
    id: "pendingDetails",
    title: "Pending registration details",
    description:
      "Calls patients whose registration or forms are incomplete and collects the missing details.",
    categories: ["outbound", "registration"],
    icon: FilePenLine,
    iconColor: "text-lime-600",
    bgColor: "bg-lime-50 dark:bg-lime-950/30",
  },
};

export const tabsConfig: TabConfig[] = [
  {
    id: "inbound",
    label: "Inbound",
    agentIds: [
      "appointmentBooking",
      "patientRegistration",
      "patientEnquiries",
      "afterHours",
      "departmentRouting",
      "callbackRequests",
    ],
  },
  {
    id: "outbound",
    label: "Outbound",
    agentIds: [
      "appointmentReminders",
      "missedAppointments",
      "followUpCalls",
      "patientFeedback",
      "reportReady",
      "pendingDetails",
    ],
  },
  {
    id: "every-call",
    label: "Every call",
    agentIds: ["staffHandoff", "transcriptsOutcomes", "multilingual"],
  },
];
